import { useMemo } from 'react';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { isValidDate } from '@frontend/schedule-helpers';
import { DATETIME_FORMATS, MIN_APPT_TYPE_DURATION_IN_MINUTES } from '../../../../../../constants';
import { useSchedulingLocationInfo } from '../../../../../../hooks/use-scheduling-location-info';
import { getDateByTimezone, isSameOrAfterDate } from '../../../../../../utils/date-helpers';
import { useManageScheduleRequestModalForm } from '../hooks/useManageScheduleRequestModalForm';
import { ReducedAppointmentType, ReducedPractitioner } from '../hooks/useReviewRequest';

type GetAvailabilityDataParams = {
  appointmentTypes?: ReducedAppointmentType[];
  providers?: ReducedPractitioner[];
  locationId?: string;
  isRealTimeAvailabilityEnabled?: boolean;
  customDateField: ReturnType<typeof useManageScheduleRequestModalForm>['customDateField'];
  customStartTimeField: ReturnType<typeof useManageScheduleRequestModalForm>['customStartTimeField'];
  customEndTimeField: ReturnType<typeof useManageScheduleRequestModalForm>['customEndTimeField'];
  values: ReturnType<typeof useManageScheduleRequestModalForm>['scheduleRequestApprovalForm']['values'];
};

export const useGetAvailabilityData = ({
  appointmentTypes,
  providers,
  locationId = '',
  isRealTimeAvailabilityEnabled,
  customDateField,
  customStartTimeField,
  customEndTimeField,
  values,
}: GetAvailabilityDataParams) => {
  const { getLocationTimeZone } = useSchedulingLocationInfo();

  const { payload, isPayloadValid } = useMemo(() => {
    const locationTimezone = getLocationTimeZone(locationId);
    const selectedApptType = appointmentTypes?.find(({ id }) => id === values.appointmentType);
    const selectedApptTypeDuration = selectedApptType?.durationMinutes || MIN_APPT_TYPE_DURATION_IN_MINUTES;
    const selectedProvider = providers?.find(({ id }) => id === values.provider);
    const providerCalendarId = selectedProvider?.calendarId || '';

    let startDateTime, endDateTime, durationMinutes;

    if (values.dateAndTime === 'custom') {
      const customDate = customDateField.value; // MM/DD/YYYY
      const customStartTime = customStartTimeField.value; // HH:mm:ss
      const customEndTime = customEndTimeField.value; // HH:mm:ss

      const startDateTimeInOfficeTimezone = getDateByTimezone(
        `${customDate} ${customStartTime}`,
        DATETIME_FORMATS.US_DATE_TIME,
        locationTimezone
      );

      const customDuration = dayjs(
        getDateByTimezone(`${customDate} ${customEndTime}`, DATETIME_FORMATS.US_DATE_TIME, locationTimezone)
      ).diff(startDateTimeInOfficeTimezone, 'minute');

      startDateTime = startDateTimeInOfficeTimezone.startOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET);
      endDateTime = startDateTimeInOfficeTimezone.endOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET);
      durationMinutes = customDuration || selectedApptTypeDuration;
    } else {
      const utcDateTime = values.dateAndTime; // Format: YYYY-MM-DDTHH:mm:ssZ
      const dateInOfficeTimezone = dayjs.utc(utcDateTime).tz(locationTimezone);
      startDateTime = dateInOfficeTimezone.startOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET);
      endDateTime = dateInOfficeTimezone.endOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET);
      durationMinutes = selectedApptTypeDuration;
    }

    const isFutureDateSelected =
      isValidDate(dayjs(startDateTime)) && isSameOrAfterDate(dayjs(startDateTime), dayjs().startOf('day'));

    return {
      payload: {
        locationId,
        calendarId: providerCalendarId,
        startDateTime: startDateTime || '',
        endDateTime: endDateTime || '',
        durationMinutes: durationMinutes,
        clientTimezone: dayjs.tz.guess(),
      },
      isPayloadValid: !!(
        locationId &&
        providerCalendarId &&
        startDateTime &&
        endDateTime &&
        durationMinutes &&
        isFutureDateSelected
      ),
    };
  }, [
    appointmentTypes,
    locationId,
    providers,
    values.appointmentType,
    values.provider,
    values.dateAndTime,
    customDateField,
    customStartTimeField,
    customEndTimeField,
    getLocationTimeZone,
  ]);

  // Fetch availability data
  const availabilityQuery = SchedulerV3.Queries.useListCalendarAvailabilitiesQuery({
    request: payload,
    options: {
      enabled: isRealTimeAvailabilityEnabled && isPayloadValid,
    },
  });

  return { availabilityQuery };
};
